import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import type {
    LoyaltyCountersResponse,
    OpenMysteryBowlResponse,
    OptInToChallengeResponse
} from '@lib/api/RewardsService';
import { type LoyaltyResponse, type Reward } from '@lib/entities/Rewards';
import { type Store } from '@lib/entities/Store';
import { loggedOut } from '@lib/redux/actions/lifecycle';
import { type DeferredAction, withDeferred } from '@lib/redux/deferred';
import { type CurrencyInteger, ZeroInteger } from '@lib/types/currency';

/**
 * Redux twin of lib/state/Rewards.ts + the async work of lib/hooks/useRewards.ts (JSAD-7433).
 * The loyalty-counter requests write `auth.loyalty` (via `auth/setLoyalty`) from the saga,
 * reading it live with `select` (JSAD-7289); this slice holds only the rewards catalogue.
 */
export interface RewardsState {
    storeId?: number;
    balance: CurrencyInteger;
    rewards: Reward[];
    isLoadingRewards: boolean;
    rewardsOnboardingShown: boolean;
    rewardsOnboardingShownWeb: boolean;
}

export type PersistedRewardsState = Pick<
    RewardsState,
    'rewardsOnboardingShown' | 'rewardsOnboardingShownWeb'
>;

const initialState: RewardsState = {
    storeId: undefined,
    balance: ZeroInteger,
    isLoadingRewards: false,
    rewards: [],
    rewardsOnboardingShown: false,
    rewardsOnboardingShownWeb: false
};

/** Saga-only request: the reducer is a no-op; the saga settles `meta.deferred`. */
const request = <P, T = void>() => ({
    reducer: (_state: unknown, _action: DeferredAction<P, T>) => {},
    prepare: withDeferred<P, T>()
});

/** `Rewards.ts:57` fetchRewards(lat?, lng?) — defaults to NYC in the saga. */
export interface FetchRewardsParams {
    lat?: number;
    lng?: number;
}

/** `Rewards.ts:53` fetchRewardsForStore(store) */
export interface FetchRewardsForStoreParams {
    store: Store;
}

/** `useRewards.ts:19` fetchLoyalty(forceFailedFetchLoyalty?) */
export interface FetchLoyaltyParams {
    forceFailedFetchLoyalty?: boolean;
}

/** `useRewards.ts:56` openMysteryBowl(id) */
export interface OpenMysteryBowlParams {
    id: string;
}

/** `useRewards.ts:204` optInToChallenge(challengeId) */
export interface OptInToChallengeParams {
    challengeId: string;
}

/** `Rewards.ts:75` — the successful end of `fetchRewards`. */
export interface RewardsLoadedPayload {
    rewards: Reward[];
    balance: CurrencyInteger;
}

const rewardsSlice = createSlice({
    name: 'rewards',
    initialState,
    reducers: {
        setRewardsOnboardingShown: (state) => {
            state.rewardsOnboardingShown = true;
        },
        setRewardsOnboardingShownWeb: (state) => {
            state.rewardsOnboardingShownWeb = true;
        },
        /** `Rewards.ts:55` — stamped after `fetchRewardsForStore` resolved. */
        setStoreId: (state, action: PayloadAction<number | undefined>) => {
            state.storeId = action.payload;
        },
        /** `Rewards.ts:65` — `fetchRewards` claimed the loading flag. */
        rewardsLoading: (state) => {
            state.isLoadingRewards = true;
        },
        rewardsLoaded: (state, action: PayloadAction<RewardsLoadedPayload>) => {
            state.rewards = action.payload.rewards;
            state.balance = action.payload.balance;
            state.isLoadingRewards = false;
        },
        /**
         * `Rewards.ts:68` — an API error put the WHOLE slice back to its defaults (onboarding
         * flags included). Ported as-is.
         */
        rewardsFailed: () => initialState,
        resetRewards: () => initialState,

        // ── Saga-only requests ─────────────────────────────────────────────────────────────
        fetchRewardsRequest: request<FetchRewardsParams>(),
        fetchRewardsForStoreRequest: request<FetchRewardsForStoreParams>(),
        fetchLoyaltyRequest: request<FetchLoyaltyParams, LoyaltyResponse | undefined>(),
        openMysteryBowlRequest: request<OpenMysteryBowlParams, OpenMysteryBowlResponse>(),
        fetchLoyaltyCountersRequest: request<void, LoyaltyCountersResponse | undefined>(),
        /** `useRewards.ts:143` — positional drop of one bowl on the live `auth.loyalty`. */
        consumeMysteryBowlRequest: request<void>(),
        /** `useRewards.ts:177` — resolves the corrected remaining bowl count. */
        reconcileAfterMysteryBowlOpenRequest: request<void, number>(),
        optInToChallengeRequest: request<OptInToChallengeParams, OptInToChallengeResponse>(),
        markChallengeModalSeenRequest: request<void>()
    },
    extraReducers: (builder) => {
        // `clearLoggedInState` → `resetRewards()` (useAuth.ts:119).
        builder.addCase(loggedOut, () => initialState);
    }
});

export const {
    setRewardsOnboardingShown,
    setRewardsOnboardingShownWeb,
    setStoreId,
    rewardsLoading,
    rewardsLoaded,
    rewardsFailed,
    resetRewards,
    fetchRewardsRequest,
    fetchRewardsForStoreRequest,
    fetchLoyaltyRequest,
    openMysteryBowlRequest,
    fetchLoyaltyCountersRequest,
    consumeMysteryBowlRequest,
    reconcileAfterMysteryBowlOpenRequest,
    optInToChallengeRequest,
    markChallengeModalSeenRequest
} = rewardsSlice.actions;

export const selectRewards = (state: { rewards: RewardsState }) => state.rewards;
export const selectRewardsStoreId = (state: { rewards: RewardsState }) => state.rewards.storeId;
export const selectIsLoadingRewards = (state: { rewards: RewardsState }) =>
    state.rewards.isLoadingRewards;

export default rewardsSlice.reducer;
